import { useState, useEffect, useCallback, useMemo } from "react";
import { Car, Loader2, Search, Download, KeySquare, Fingerprint } from "lucide-react";
import { apiFetch } from "@/portal/api";
import { fmtCZK } from "@/portal/components/adminShared";
import { toast } from "@/components/ui/sonner";

const STATUS = { free: { label: "Volný", cls: "bg-emerald-500/15 text-emerald-300" }, rented: { label: "Pronajatý", cls: "bg-orange-500/15 text-orange-300" }, service: { label: "Servis", cls: "bg-amber-500/15 text-amber-300" } };
const fmtD = (s) => (s ? new Date(s.length <= 10 ? s + "T00:00:00" : s).toLocaleDateString("cs-CZ", { day: "numeric", month: "short", year: "numeric" }) : "—");
const noVat = (v) => (v == null ? null : Math.round(Number(v) / 1.21));

const SummaryCard = ({ label, value, sub, icon: Icon, tone = "" }) => (
  <div className="glass rounded-2xl p-5" data-testid={`vehreg-card-${label}`}>
    <div className={`flex items-center gap-2 text-xs font-medium mb-1 ${tone || "text-zinc-400"}`}><Icon className="w-4 h-4" /> {label}</div>
    <p className={`text-2xl font-bold font-['Cabinet_Grotesk'] ${tone || ""}`}>{value}</p>
    {sub && <p className="text-[11px] text-zinc-500 mt-0.5">{sub}</p>}
  </div>
);

const VehicleRegistryTab = ({ token }) => {
  const [cars, setCars] = useState(null);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all"); // all | rented | ident

  const load = useCallback(async () => {
    try { const d = await apiFetch("/accounting/vehicles", { token }); setCars(d.cars || []); }
    catch (e) { toast.error(e.message); }
    finally { setLoading(false); }
  }, [token]);
  useEffect(() => { load(); }, [load]);

  const rows = useMemo(() => {
    if (!cars) return [];
    const ql = q.trim().toLowerCase();
    return cars.filter((c) => {
      if (filter === "rented" && !c.is_rented) return false;
      if (filter === "ident" && !(c.license_plate || c.vin)) return false;
      if (!ql) return true;
      return [c.brand, c.model, c.license_plate, c.vin, c.client_name, c.partner_name].filter(Boolean).some((v) => String(v).toLowerCase().includes(ql));
    });
  }, [cars, q, filter]);

  const totals = useMemo(() => {
    if (!cars) return {};
    return {
      total: cars.length,
      rented: cars.filter((c) => c.is_rented).length,
      ident: cars.filter((c) => c.license_plate && c.vin).length,
      invoiced: cars.reduce((s, c) => s + (Number(c.rental_amount) || 0), 0),
    };
  }, [cars]);

  const exportCsv = () => {
    const head = ["Značka", "Model", "Rok", "SPZ", "VIN", "Stav", "Pronajato komu", "Pronájem do", "Fakturace/měs s DPH", "Fakturace/měs bez DPH", "Náklad Araver/měs bez DPH", "Partner", "Výplata partnera/měs s DPH"];
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const lines = rows.map((c) => [c.brand, c.model, c.year, c.license_plate, c.vin, STATUS[c.status]?.label || c.status, c.client_name, c.rental_until, c.rental_amount, noVat(c.rental_amount), c.araver_cost, c.partner_name, c.partner_monthly].map(esc).join(";"));
    const csv = "\uFEFF" + [head.map(esc).join(";"), ...lines].join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8;" }));
    const a = document.createElement("a");
    a.href = url; a.download = `evidence-vozidel-${new Date().toISOString().slice(0, 10)}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="w-7 h-7 text-orange-400 animate-spin" /></div>;

  const FilterBtn = ({ k, label }) => (
    <button onClick={() => setFilter(k)} className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${filter === k ? "bg-orange-500/15 text-orange-300 border-orange-500/40" : "border-zinc-700 text-zinc-400 hover:text-zinc-200"}`} data-testid={`vehreg-filter-${k}`}>{label}</button>
  );

  return (
    <div className="space-y-6" data-testid="vehicle-registry-tab">
      <div className="flex flex-wrap items-center gap-3">
        <Car className="w-6 h-6 text-orange-400" />
        <div className="mr-auto">
          <h2 className="text-xl font-bold font-['Cabinet_Grotesk']">Evidence vozidel</h2>
          <p className="text-xs text-zinc-500">Všechna auta u nás – SPZ, VIN, komu je pronajaté a měsíční finance.</p>
        </div>
        <button onClick={exportCsv} className="inline-flex items-center gap-2 text-sm px-3 py-2 rounded-full border border-zinc-700 text-zinc-300 hover:text-white hover:border-zinc-500" data-testid="vehreg-export"><Download className="w-4 h-4" /> Stáhnout (Excel/CSV)</button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <SummaryCard label="Vozů celkem" value={totals.total} icon={Car} />
        <SummaryCard label="Pronajatých" value={totals.rented} icon={KeySquare} tone="text-orange-300" />
        <SummaryCard label="Má SPZ i VIN" value={totals.ident} icon={Fingerprint} />
        <SummaryCard label="Fakturace / měs" value={fmtCZK(totals.invoiced)} sub="součet pronájmů (s DPH)" icon={Download} tone="text-emerald-300" />
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="w-4 h-4 text-zinc-500 absolute left-3 top-1/2 -translate-y-1/2" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Hledat SPZ, VIN, model, klienta…" className="w-full bg-zinc-900/60 border border-zinc-700 rounded-xl pl-9 pr-3 py-2 text-sm focus:border-orange-500/60 outline-none" data-testid="vehreg-search" />
        </div>
        <FilterBtn k="all" label="Vše" />
        <FilterBtn k="rented" label="Pronajaté" />
        <FilterBtn k="ident" label="Má SPZ/VIN" />
        <span className="text-xs text-zinc-500 ml-auto">{rows.length} / {cars.length} vozů</span>
      </div>

      {rows.length === 0 ? (
        <div className="glass rounded-2xl p-12 text-center text-zinc-500" data-testid="vehreg-empty">Žádná vozidla neodpovídají filtru.</div>
      ) : (
        <div className="glass rounded-2xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-[1080px] w-full text-sm border-separate border-spacing-0">
              <thead className="bg-zinc-900/60 text-zinc-400">
                <tr>
                  {["Vůz", "SPZ", "VIN", "Stav", "Pronajato", "Fakturace / měs", "Náklad Araver", "Výplata partnera"].map((h, i) => (
                    <th key={i} className={`text-left font-medium px-3 py-2.5 text-xs uppercase tracking-wider ${i >= 5 ? "text-right" : ""}`}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((c) => {
                  const invNoVat = noVat(c.rental_amount);
                  return (
                    <tr key={c.id} className="border-t border-zinc-800 hover:bg-zinc-800/30" data-testid={`vehreg-row-${c.id}`}>
                      <td className="px-3 py-2.5 border-t border-zinc-800">
                        <div className="font-medium text-zinc-100">{c.brand} {c.model}</div>
                        {c.year && <div className="text-[11px] text-zinc-500">{c.year}</div>}
                      </td>
                      <td className="px-3 py-2.5 border-t border-zinc-800 font-mono text-zinc-200" data-testid={`vehreg-spz-${c.id}`}>{c.license_plate || <span className="text-zinc-600">—</span>}</td>
                      <td className="px-3 py-2.5 border-t border-zinc-800 font-mono text-xs text-zinc-300" data-testid={`vehreg-vin-${c.id}`}>{c.vin || <span className="text-zinc-600">—</span>}</td>
                      <td className="px-3 py-2.5 border-t border-zinc-800"><span className={`text-[11px] px-2 py-0.5 rounded-full ${STATUS[c.status]?.cls || "bg-zinc-700 text-zinc-300"}`}>{STATUS[c.status]?.label || c.status}</span></td>
                      <td className="px-3 py-2.5 border-t border-zinc-800">
                        {c.is_rented ? (<><div className="text-zinc-200">{c.client_name || "—"}</div><div className="text-[11px] text-zinc-500">do {fmtD(c.rental_until)}</div></>) : <span className="text-zinc-600">volný</span>}
                      </td>
                      <td className="px-3 py-2.5 border-t border-zinc-800 text-right">
                        {c.rental_amount != null ? (<><div className="text-zinc-100 font-medium">{fmtCZK(c.rental_amount)}</div><div className="text-[11px] text-zinc-500">{fmtCZK(invNoVat)} bez DPH</div></>) : <span className="text-zinc-600">—</span>}
                      </td>
                      <td className="px-3 py-2.5 border-t border-zinc-800 text-right text-zinc-300">{c.araver_cost != null ? <>{fmtCZK(c.araver_cost)}<div className="text-[11px] text-zinc-600">bez DPH</div></> : <span className="text-zinc-600">—</span>}</td>
                      <td className="px-3 py-2.5 border-t border-zinc-800 text-right text-zinc-300">{c.partner_monthly != null ? <>{fmtCZK(c.partner_monthly)}<div className="text-[11px] text-zinc-600">{c.partner_name || ""}</div></> : <span className="text-zinc-600">—</span>}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default VehicleRegistryTab;
